import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CLI_LIKE_CLIENTS } from 'consts/authentication';
import { ROUTES } from 'routes/consts';
import { getPendingResearchPreviewConnect } from 'utils/researchPreviewConnect';
import { getInitClientFromSessionStorage } from 'utils/url';
import { DeviceCodeVerify } from '../DeviceCodeVerify';
import { SuccessMessage } from '../SuccessMessage';
import { SuccessMessageProps } from '../SuccessMessage/types';

/** Show "All set!" briefly before returning to the integrations page. */
const INTEGRATION_REDIRECT_DELAY_MS = 1000;

/**
 * Integration OAuth connects are the only flow that lands on the generic success
 * route; other SuccessPage uses (e.g. sign-in) pass a different key and must not be
 * redirected by a lingering pending-connect marker.
 */
const INTEGRATION_SUCCESS_KEY = 'generic-success';

/** The external-client sign-in success route uses this translation key. */
const EXTERNAL_CLIENT_SUCCESS_KEY = 'success-signin-external-client';

export const SuccessPage = ({
  translationKey,
  className,
}: SuccessMessageProps) => {
  const navigate = useNavigate();
  const [isCodeConfirmed, setIsCodeConfirmed] = useState(false);

  // When an integration connect lands here, briefly show the success message and
  // then return to the integrations page, where the research-preview auto-enable
  // consumes the pending-connect marker.
  useEffect(() => {
    // Only the integration-connect success may redirect — guards other success
    // routes from being hijacked by a stale marker.
    if (translationKey !== INTEGRATION_SUCCESS_KEY) {
      return;
    }

    // Stale/expired markers are ignored (and cleared) by the getter, so a
    // cancelled connect can't later hijack an unrelated success page.
    if (!getPendingResearchPreviewConnect()) {
      return;
    }

    const timeout = setTimeout(
      () => navigate(ROUTES.INTEGRATIONS),
      INTEGRATION_REDIRECT_DELAY_MS,
    );

    return () => clearTimeout(timeout);
  }, [navigate, translationKey]);

  const isCliDeviceLogin =
    translationKey === EXTERNAL_CLIENT_SUCCESS_KEY &&
    CLI_LIKE_CLIENTS.includes(getInitClientFromSessionStorage());

  if (isCliDeviceLogin && !isCodeConfirmed) {
    return <DeviceCodeVerify onConfirmed={() => setIsCodeConfirmed(true)} />;
  }

  return (
    <SuccessMessage translationKey={translationKey} className={className} />
  );
};
